/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Wraps the search terms in <mark> inside rendered HTML (titles and previews on the search results page). Works on
// text nodes, so markup, attributes and links (mentions, hashtags, URLs) are never touched. Whole words only, any
// case; a hashtag query matches the bare word.
export function highlightHtml(html: string, query: string | null | undefined): string {
  const terms = (query ?? '').split(/\s+/).map((term) => term.replace(/^#/, '')).filter(Boolean);
  if (!html || !terms.length) return html;

  // \b only works next to word characters, so "c++" is bounded by lookarounds instead.
  const pattern = new RegExp(`(?<![\\p{L}\\p{N}_])(${terms.map(escapeRegExp).join('|')})(?![\\p{L}\\p{N}_])`, 'giu');
  const template = document.createElement('template');
  template.innerHTML = html;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.parentElement?.closest('a, mark') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  textNodes.forEach((node) => {
    const text = node.data;
    if (!text.match(pattern)) return;
    const fragment = document.createDocumentFragment();
    let last = 0;
    Array.from(text.matchAll(pattern)).forEach((match) => {
      fragment.append(text.slice(last, match.index));
      const mark = document.createElement('mark');
      mark.textContent = match[0];
      fragment.append(mark);
      last = match.index + match[0].length;
    });
    fragment.append(text.slice(last));
    node.replaceWith(fragment);
  });
  return template.innerHTML;
}
